<template>
  <aside :class="['organizer-nav', { 'is-open': mobileOpen, 'is-drawer': drawerMode }]" :style="desktopContainerStyle">
    <div class="organizer-nav__backdrop" @click="$emit('close')"></div>

    <div class="organizer-nav__panel glass-surface" :style="desktopPanelStyle">
      <div v-if="!drawerMode" class="organizer-nav__brand">
        <router-link class="organizer-nav__logo" :to="brandRoute">
          <img src="/static/LOGOFOMO26_pro.png" alt="Fomo Planes" />
        </router-link>

        <button class="organizer-nav__close" type="button" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <button v-else class="organizer-nav__close organizer-nav__close--floating" type="button" @click="$emit('close')">
        <i class="fas fa-times"></i>
      </button>

      <div class="organizer-nav__section">
        <p class="organizer-nav__eyebrow">{{ workspaceEyebrow }}</p>
        <div class="organizer-nav__track" :style="{ '--active-index': globalActiveIndex }">
          <div class="organizer-nav__indicator" :class="{ 'is-visible': globalActiveIndex >= 0 }" aria-hidden="true">
            <span class="organizer-nav__indicator-glow"></span>
            <span class="organizer-nav__indicator-clip">
              <span class="organizer-nav__indicator-ring"></span>
            </span>
            <span class="organizer-nav__indicator-plate"></span>
          </div>

          <router-link v-for="item in resolvedGlobalItems" :key="item.label" class="organizer-nav__link" :class="{ 'is-active': item.active }" :to="item.to" @click="$emit('close')">
            <i :class="item.icon"></i>
            <span class="organizer-nav__link-copy">{{ item.label }}</span>
          </router-link>
        </div>
      </div>

      <div v-if="resolvedContextItems.length" class="organizer-nav__section organizer-nav__section--context">
        <div class="organizer-nav__context-head">
          <p class="organizer-nav__eyebrow">{{ contextEyebrow }}</p>
          <strong>{{ resolvedContextLabel }}</strong>
        </div>

        <div class="organizer-nav__track" :style="{ '--active-index': contextActiveIndex }">
          <div class="organizer-nav__indicator" :class="{ 'is-visible': contextActiveIndex >= 0 }" aria-hidden="true">
            <span class="organizer-nav__indicator-glow"></span>
            <span class="organizer-nav__indicator-clip">
              <span class="organizer-nav__indicator-ring"></span>
            </span>
            <span class="organizer-nav__indicator-plate"></span>
          </div>

          <router-link v-for="item in resolvedContextItems" :key="item.label" class="organizer-nav__link organizer-nav__link--context" :class="{ 'is-active': item.active }" :to="item.to" @click="$emit('close')">
            <i :class="item.icon"></i>
            <span class="organizer-nav__link-copy">{{ item.label }}</span>
          </router-link>
        </div>
      </div>

      <div class="organizer-nav__footer">
        <div class="organizer-nav__session">
          <small>Sesion activa</small>
          <strong>{{ identityLabel }}</strong>
        </div>

        <button v-if="logoutRoute === null" class="organizer-nav__logout" type="button" @click="handleLogout">
          <i class="fas fa-right-from-bracket"></i>
          <span>{{ logoutLabel }}</span>
        </button>

        <router-link v-else class="organizer-nav__logout" :to="logoutRoute" @click="$emit('close')">
          <i class="fas fa-right-from-bracket"></i>
          <span>{{ logoutLabel }}</span>
        </router-link>
      </div>
    </div>
  </aside>
</template>

<script>
export default {
  name: 'OrganizerWorkspaceNav',

  data() {
    return {
      bodyLockActive: false,
      previousBodyOverflow: '',
      desktopScrollOffset: 0,
      desktopUsesElementScroll: false,
      desktopScrollElement: null,
      desktopWorkspaceRoot: null,
    }
  },

  props: {
    mobileOpen: {
      type: Boolean,
      default: false,
    },

    currentEvent: {
      type: Object,
      default: null,
    },

    eventSlug: {
      type: [String, Number],
      default: '',
    },

    currentSection: {
      type: String,
      default: '',
    },

    drawerMode: {
      type: Boolean,
      default: false,
    },

    brandRoute: {
      type: [Object, String],
      default: () => ({ name: 'events2' }),
    },

    workspaceEyebrow: {
      type: String,
      default: 'Workspace',
    },

    workspaceItems: {
      type: Array,
      default: () => [],
    },

    contextEyebrow: {
      type: String,
      default: 'Evento actual',
    },

    contextLabel: {
      type: String,
      default: '',
    },

    contextItems: {
      type: Array,
      default: () => [],
    },

    identityLabelOverride: {
      type: String,
      default: '',
    },

    logoutLabel: {
      type: String,
      default: 'Salir',
    },

    logoutRoute: {
      type: [Object, String],
      default: () => ({ name: 'LogOut' }),
    },
  },

  emits: ['close', 'logout'],

  mounted() {
    this.syncBodyLock()
    this.setupDesktopScrollSync()
  },

  beforeUnmount() {
    this.releaseBodyLock()
    this.teardownDesktopScrollSync()
  },

  computed: {
    identityLabel() {
      if (this.identityLabelOverride) return this.identityLabelOverride
      return window.localStorage.getItem('username') || 'Organizador'
    },

    eventLabel() {
      return (this.currentEvent && this.currentEvent.name) || 'Evento cargando'
    },

    resolvedContextLabel() {
      return this.contextLabel || this.eventLabel
    },

    globalItems() {
      return [
        { label: 'Eventos', icon: 'fas fa-calendar-alt', to: { name: 'events2' }, active: this.$route.name === 'events2' },
        { label: 'Crear evento', icon: 'fas fa-plus-circle', to: { name: 'event-creation' }, active: this.$route.name === 'event-creation' },
        { label: 'Finanzas', icon: 'fas fa-chart-line', to: { name: 'finances2' }, active: this.$route.name === 'finances2' },
        { label: 'Herramientas', icon: 'fas fa-toolbox', to: { name: 'tools2' }, active: this.$route.name === 'tools2' },
      ]
    },

    resolvedGlobalItems() {
      return Array.isArray(this.workspaceItems) && this.workspaceItems.length ? this.workspaceItems : this.globalItems
    },

    globalActiveIndex() {
      return this.resolvedGlobalItems.findIndex((item) => item.active)
    },

    eventItems() {
      if (!this.eventSlug) {
        return []
      }

      const routeBase = { name: 'EventHostView2', params: { slug: this.eventSlug } }
      return [
        { label: 'Resumen', icon: 'fas fa-chart-pie', to: { ...routeBase, query: {} }, active: this.currentSection === 'Resumen' },
        { label: 'Editar', icon: 'fas fa-pen', to: { ...routeBase, query: { section: 'editar' } }, active: this.currentSection === 'Editar' },
        { label: 'Etapas', icon: 'fas fa-layer-group', to: { ...routeBase, query: { section: 'etapas' } }, active: this.currentSection === 'Etapas' },
        { label: 'Códigos y promos', icon: 'fas fa-bullhorn', to: { ...routeBase, query: { section: 'codigos-promos' } }, active: this.currentSection === 'Códigos y promos' },
        { label: 'Cortesías', icon: 'fas fa-gift', to: { ...routeBase, query: { section: 'cortesias' } }, active: this.currentSection === 'Cortesías' },
        { label: 'Asistentes', icon: 'fas fa-user-check', to: { ...routeBase, query: { section: 'asistentes' } }, active: this.currentSection === 'Asistentes' },
        { label: 'Accesos', icon: 'fas fa-qrcode', to: { ...routeBase, query: { section: 'accesos' } }, active: this.currentSection === 'Accesos' },
        { label: 'Finanzas', icon: 'fas fa-wallet', to: { ...routeBase, query: { section: 'finanzas' } }, active: this.currentSection === 'Finanzas' },
      ]
    },

    eventActiveIndex() {
      return this.eventItems.findIndex((item) => item.active)
    },

    resolvedContextItems() {
      return Array.isArray(this.contextItems) && this.contextItems.length ? this.contextItems : this.eventItems
    },

    contextActiveIndex() {
      return this.resolvedContextItems.findIndex((item) => item.active)
    },

    desktopContainerStyle() {
      if (this.drawerMode) {
        return null
      }

      return {
        position: 'relative',
        top: 'auto',
        bottom: 'auto',
        left: 'auto',
        right: 'auto',
        height: 'auto',
        maxHeight: 'none',
        overflow: 'visible',
        transform: this.desktopUsesElementScroll ? `translateY(${-this.desktopScrollOffset}px)` : 'none',
        willChange: this.desktopUsesElementScroll ? 'transform' : 'auto',
      }
    },

    desktopPanelStyle() {
      if (this.drawerMode) {
        return null
      }

      return {
        position: 'relative',
        top: 'auto',
        bottom: 'auto',
        left: 'auto',
        right: 'auto',
        height: 'auto',
        maxHeight: 'none',
        overflow: 'visible',
        transform: 'none',
      }
    },
  },

  watch: {
    mobileOpen() {
      this.syncBodyLock()
    },

    drawerMode() {
      this.syncBodyLock()
    },
  },

  methods: {
    handleLogout() {
      this.$emit('logout')
      this.$emit('close')
    },

    syncBodyLock() {
      if (typeof document === 'undefined') {
        return
      }

      const shouldLock = this.drawerMode && this.mobileOpen
      if (shouldLock && !this.bodyLockActive) {
        this.previousBodyOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        this.bodyLockActive = true
        return
      }

      if (!shouldLock) {
        this.releaseBodyLock()
      }
    },

    releaseBodyLock() {
      if (typeof document === 'undefined' || !this.bodyLockActive) {
        return
      }

      document.body.style.overflow = this.previousBodyOverflow
      this.bodyLockActive = false
    },

    isDesktopViewport() {
      return typeof window !== 'undefined' && window.matchMedia('(min-width: 981px)').matches
    },

    resetDesktopScrollSync() {
      this.desktopUsesElementScroll = false
      this.desktopScrollOffset = 0
      this.desktopScrollElement = null
    },

    setupDesktopScrollSync() {
      if (typeof window === 'undefined' || typeof document === 'undefined' || this.drawerMode) {
        return
      }

      this.desktopWorkspaceRoot = this.$el.closest('.workspace-v2')
      this.handleDesktopResize = () => {
        if (!this.isDesktopViewport()) {
          this.resetDesktopScrollSync()
        }
      }

      this.handleDesktopWindowScroll = () => {
        if (!this.isDesktopViewport()) {
          return
        }

        this.resetDesktopScrollSync()
      }

      this.handleDesktopCapturedScroll = (event) => {
        if (!this.isDesktopViewport()) {
          return
        }

        const target = event.target
        if (!(target instanceof Element)) {
          return
        }

        if (!this.desktopWorkspaceRoot || !this.desktopWorkspaceRoot.contains(target) || this.$el.contains(target)) {
          return
        }

        const scrollHeight = target.scrollHeight || 0
        const clientHeight = target.clientHeight || 0
        const scrollTop = target.scrollTop || 0
        if (scrollHeight <= clientHeight + 1) {
          return
        }

        if (target === this.desktopScrollElement || scrollTop > 0) {
          this.desktopUsesElementScroll = true
          this.desktopScrollElement = target
          this.desktopScrollOffset = scrollTop
        }

        if (target === this.desktopScrollElement && scrollTop <= 0) {
          this.desktopScrollOffset = 0
        }
      }

      window.addEventListener('resize', this.handleDesktopResize, { passive: true })
      window.addEventListener('scroll', this.handleDesktopWindowScroll, { passive: true })
      document.addEventListener('scroll', this.handleDesktopCapturedScroll, true)
    },

    teardownDesktopScrollSync() {
      if (typeof window === 'undefined' || typeof document === 'undefined' || this.drawerMode) {
        return
      }

      window.removeEventListener('resize', this.handleDesktopResize)
      window.removeEventListener('scroll', this.handleDesktopWindowScroll)
      document.removeEventListener('scroll', this.handleDesktopCapturedScroll, true)
      this.resetDesktopScrollSync()
    },
  },
}
</script>

<style scoped>
.organizer-nav {
  position: relative !important;
  align-self: start;
  height: auto;
  width: 100%;
  min-width: 0;
}

.organizer-nav__backdrop {
  display: none;
}

.organizer-nav__panel {
  position: relative !important;
  display: grid;
  gap: 20px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  padding: 22px 18px 18px;
  border-radius: 30px;
  border: 1px solid rgba(198, 208, 230, 0.12);
  box-shadow:
    0 24px 52px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  background:
    radial-gradient(circle at top left, rgba(225, 231, 241, 0.1), transparent 18%),
    radial-gradient(circle at 82% 14%, rgba(111, 92, 145, 0.12), transparent 24%),
    linear-gradient(135deg, rgba(193, 201, 216, 0.09), rgba(170, 160, 196, 0.04)),
    rgba(9, 8, 14, 0.72);
  backdrop-filter: blur(28px) saturate(146%);
  -webkit-backdrop-filter: blur(28px) saturate(146%);
}

.organizer-nav__panel::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background:
    linear-gradient(130deg, rgba(255, 255, 255, 0.12), transparent 18%, transparent 72%, rgba(170, 184, 210, 0.05)),
    radial-gradient(circle at top left, rgba(255, 255, 255, 0.06), transparent 22%);
  opacity: 0.54;
}

.organizer-nav__brand,
.organizer-nav__context-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.organizer-nav__footer {
  display: grid;
  gap: 12px;
  align-items: stretch;
}

.organizer-nav__logo img {
  display: block;
  width: 214px;
  max-width: 100%;
  filter: drop-shadow(0 10px 24px rgba(142, 111, 198, 0.22));
}

.organizer-nav__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 0 18px;
  min-width: 0;
  width: 100%;
  text-decoration: none;
  border-bottom: 1px solid rgba(198, 208, 230, 0.12);
}

.organizer-nav__close {
  display: none;
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  color: #eef4ff;
}

.organizer-nav__section {
  display: grid;
  gap: 8px;
  padding: 0;
}

.organizer-nav__track {
  --nav-item-height: 54px;
  position: relative;
  isolation: isolate;
  display: grid;
  gap: 10px;
  min-width: 0;
}

.organizer-nav__indicator {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: var(--nav-item-height);
  border-radius: 18px;
  opacity: 0;
  overflow: hidden;
  z-index: 1;
  pointer-events: none;
  transform: translateY(calc(var(--active-index, 0) * (var(--nav-item-height) + 10px)));
  transition: transform 520ms cubic-bezier(0.34, 1.2, 0.64, 1), opacity 180ms ease;
}

.organizer-nav__indicator.is-visible {
  opacity: 1;
}

.organizer-nav__indicator-glow,
.organizer-nav__indicator-clip,
.organizer-nav__indicator-plate,
.organizer-nav__indicator-ring {
  position: absolute;
  inset: 0;
  border-radius: inherit;
}

.organizer-nav__indicator-glow {
  inset: 0;
  background: radial-gradient(circle at 50% 50%, rgba(228, 234, 244, 0.14), rgba(120, 108, 150, 0.08) 42%, transparent 72%);
  filter: blur(8px);
  opacity: 0.14;
}

.organizer-nav__indicator-clip {
  overflow: hidden;
  inset: 0;
}

.organizer-nav__indicator-ring {
  inset: auto;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  border-radius: 50%;
  animation: organizer-nav-spin 4.5s linear infinite;
  background: conic-gradient(
    from 0deg,
    #373641 0%,
    #676571 14%,
    #b8beca 24%,
    #e9edf5 29%,
    #ffffff 31%,
    #d6c8ec 32.5%,
    #d7e0f3 34%,
    #a5abb8 35.5%,
    #373641 50%,
    #676571 64%,
    #b8beca 74%,
    #e9edf5 79%,
    #ffffff 81%,
    #d6c8ec 82.5%,
    #d7e0f3 84%,
    #a5abb8 85.5%,
    #373641 100%
  );
}

.organizer-nav__indicator-plate {
  inset: 2px;
  background:
    radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.08), transparent 34%),
    linear-gradient(180deg, rgba(24, 23, 34, 0.9), rgba(10, 10, 16, 0.95));
  border: 1px solid rgba(255, 255, 255, 0.04);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  z-index: 1;
}

.organizer-nav__section--context {
  padding-top: 18px;
  border-top: 1px solid rgba(195, 207, 222, 0.1);
}

.organizer-nav__eyebrow,
.organizer-nav__session small {
  margin: 0 0 2px;
  color: rgba(214, 223, 240, 0.54);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

.organizer-nav__context-head strong,
.organizer-nav__session strong {
  color: #f4f8ff;
  font-size: 1.22rem;
  line-height: 1.02;
  max-width: 200px;
  font-family: 'Syne', sans-serif;
  letter-spacing: -0.05em;
}

.organizer-nav__link,
.organizer-nav__logout {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  min-height: 54px;
  padding: 0 14px;
  border-radius: 18px;
  color: rgba(230, 236, 245, 0.88);
  text-decoration: none;
  border: 1px solid transparent;
  background: transparent;
  box-shadow: none;
  transition: transform 0.18s ease, color 0.18s ease;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.94rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  white-space: normal;
  position: relative;
  isolation: isolate;
  z-index: 2;
}

.organizer-nav__link::before,
.organizer-nav__logout::before {
  content: '';
  position: absolute;
  inset: 2px;
  border-radius: 16px;
  background:
    radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.09), transparent 34%),
    linear-gradient(180deg, rgba(22, 21, 32, 0.88), rgba(10, 10, 16, 0.94));
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.1),
    inset 0 -12px 18px rgba(117, 103, 149, 0.05);
  z-index: -1;
}

.organizer-nav__link:hover,
.organizer-nav__logout:hover,
.organizer-nav__link.is-active {
  transform: translateX(2px);
  color: #f6fbff;
}

.organizer-nav__link:hover::before,
.organizer-nav__logout:hover::before,
.organizer-nav__link.is-active::before {
  background:
    radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.1), transparent 34%),
    linear-gradient(180deg, rgba(26, 24, 36, 0.9), rgba(11, 10, 18, 0.96));
}

.organizer-nav__link.is-active::before,
.organizer-nav__link--context.is-active::before {
  opacity: 1;
  inset: 2px;
  border: 1px solid rgba(255, 255, 255, 0.04);
  background:
    radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.09), transparent 34%),
    linear-gradient(180deg, rgba(24, 23, 34, 0.92), rgba(10, 10, 16, 0.97));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    inset 0 -10px 16px rgba(117, 103, 149, 0.04);
}

.organizer-nav__link--context.is-active {
  color: #f9fbff;
}

.organizer-nav__link i,
.organizer-nav__logout i {
  flex: 0 0 18px;
  width: 18px;
  text-align: center;
  font-size: 0.98rem;
}

.organizer-nav__footer {
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid rgba(195, 207, 222, 0.1);
}

.organizer-nav__link-copy,
.organizer-nav__logout span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.organizer-nav__logout {
  justify-content: flex-start;
}

@media (min-width: 981px) {
  .organizer-nav {
    position: relative !important;
    top: auto !important;
    bottom: auto !important;
    left: auto !important;
    right: auto !important;
    height: auto !important;
    max-height: none !important;
    transform: none !important;
    overflow: visible !important;
  }

  .organizer-nav__panel {
    position: relative !important;
    top: auto !important;
    bottom: auto !important;
    left: auto !important;
    right: auto !important;
    height: auto !important;
    max-height: none !important;
    transform: none !important;
    overflow: hidden !important;
  }
}

@keyframes organizer-nav-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.organizer-nav__session {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.organizer-nav__session strong {
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.organizer-nav.is-drawer {
  position: fixed;
  inset: 0;
  z-index: 120;
  isolation: isolate;
  pointer-events: none;
}

.organizer-nav.is-drawer .organizer-nav__backdrop {
  display: block;
  position: fixed;
  inset: 0;
  z-index: 0;
  background:
    radial-gradient(circle at 18% 8%, rgba(116, 214, 200, 0.12), transparent 26%),
    radial-gradient(circle at 82% 14%, rgba(255, 255, 255, 0.06), transparent 24%),
    linear-gradient(180deg, rgba(5, 8, 14, 0.24), rgba(4, 6, 12, 0.54));
  backdrop-filter: blur(14px) saturate(122%);
  -webkit-backdrop-filter: blur(14px) saturate(122%);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.organizer-nav.is-drawer .organizer-nav__panel {
  position: fixed !important;
  inset: 10px auto 10px 10px !important;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: min(308px, calc(100vw - 20px));
  min-height: calc(100dvh - 20px);
  height: calc(100dvh - 20px);
  max-height: calc(100dvh - 20px);
  overflow: auto !important;
  border-radius: 30px;
  box-sizing: border-box;
  gap: 12px;
  padding: max(10px, calc(10px + env(safe-area-inset-top))) 14px max(16px, calc(16px + env(safe-area-inset-bottom)));
  overscroll-behavior: contain;
  border: 1px solid rgba(210, 221, 236, 0.14);
  background:
    radial-gradient(circle at top left, rgba(240, 245, 255, 0.16), transparent 22%),
    radial-gradient(circle at 86% 12%, rgba(116, 214, 200, 0.14), transparent 24%),
    linear-gradient(160deg, rgba(255, 255, 255, 0.13), rgba(255, 255, 255, 0.025)),
    rgba(10, 11, 18, 0.42);
  box-shadow:
    0 26px 72px rgba(0, 0, 0, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.16),
    inset 0 -14px 28px rgba(72, 96, 142, 0.08);
  backdrop-filter: blur(34px) saturate(168%);
  -webkit-backdrop-filter: blur(34px) saturate(168%);
  opacity: 0;
  transform: translate3d(-18px, 0, 0) scale(0.985) !important;
  transition: transform 0.24s ease, opacity 0.24s ease;
}

.organizer-nav.is-drawer .organizer-nav__panel::before {
  inset: 1px;
  opacity: 0.82;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.16), transparent 18%, transparent 68%, rgba(116, 214, 200, 0.08)),
    radial-gradient(circle at top left, rgba(255, 255, 255, 0.1), transparent 26%);
}

.organizer-nav.is-drawer .organizer-nav__panel::after {
  content: '';
  position: absolute;
  inset: auto 12% -26px 12%;
  height: 54px;
  border-radius: 999px;
  background: radial-gradient(circle, rgba(116, 214, 200, 0.18), rgba(116, 214, 200, 0) 72%);
  filter: blur(16px);
  pointer-events: none;
  opacity: 0.78;
}

.organizer-nav.is-drawer .organizer-nav__link::before,
.organizer-nav.is-drawer .organizer-nav__logout::before {
  background:
    radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.14), transparent 34%),
    linear-gradient(180deg, rgba(22, 21, 32, 0.22), rgba(10, 10, 16, 0.34));
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    inset 0 -12px 18px rgba(117, 103, 149, 0.04);
  backdrop-filter: blur(18px) saturate(138%);
  -webkit-backdrop-filter: blur(18px) saturate(138%);
}

.organizer-nav.is-drawer .organizer-nav__link:hover::before,
.organizer-nav.is-drawer .organizer-nav__logout:hover::before,
.organizer-nav.is-drawer .organizer-nav__link.is-active::before,
.organizer-nav.is-drawer .organizer-nav__link--context.is-active::before {
  background:
    radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.16), transparent 34%),
    linear-gradient(180deg, rgba(24, 23, 34, 0.28), rgba(10, 10, 16, 0.42));
}

.organizer-nav.is-drawer .organizer-nav__logo {
  display: none;
}

.organizer-nav.is-drawer .organizer-nav__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 14px;
  margin-left: auto;
  pointer-events: auto;
}

.organizer-nav.is-drawer .organizer-nav__close--floating {
  position: absolute;
  top: max(10px, calc(10px + env(safe-area-inset-top)));
  right: 14px;
  z-index: 4;
}

.organizer-nav.is-drawer .organizer-nav__footer {
  margin-top: 10px;
}

.organizer-nav.is-drawer .organizer-nav__logo-copy strong {
  font-size: 1.72rem;
}

.organizer-nav.is-drawer.is-open {
  pointer-events: auto;
}

.organizer-nav.is-drawer.is-open .organizer-nav__backdrop {
  opacity: 1;
}

.organizer-nav.is-drawer.is-open .organizer-nav__panel {
  opacity: 1;
  transform: translate3d(0, 0, 0) scale(1) !important;
}
</style>
